<script setup>
import { useFlagStore } from '@/stores/flag';
import {
  Banner,
  Button,
  DropdownPanel,
  Section,
  SectionHeader,
  SelectList,
} from '@dashhudson/dashing-ui';
import { useRoute, useRouter } from 'vue-router';
import { useSocialListeningComparisonsStore } from '@/stores/social-listening-comparisons';
import { computed, ref, watch } from 'vue';
import { comparisonViewed } from '@/app/socialListening/utils/mixpanel-events';
import MentionsTrendComparison from '@/app/socialListening/components/comparisons/MentionsTrendComparison.vue';
import MentionsBreakdown from '@/app/socialListening/components/comparisons/MentionsBreakdown.vue';
import ComparisonSummary from '@/app/socialListening/components/comparisons/ComparisonSummary.vue';
import SentimentAnalysisComparison from '@/app/socialListening/components/comparisons/SentimentAnalysisComparison.vue';
import {
  SOCIAL_LISTENING_ROUTE_NAMES,
  TOPIC_COMPARISONS_TOOLTIPS,
} from '@/app/socialListening/constants';
import { useSocialListeningTopicsStore } from '@/stores/social-listening-topics';
import TopPerformingPostsPreview from '@/app/socialListening/components/shared/TopPerformingPostsPreview.vue';
import Demographics from '@/app/socialListening/components/comparisons/Demographics.vue';
import ComparisonPageHeader from '@/app/socialListening/components/comparisons/ComparisonPageHeader.vue';
import ComparisonFilterPanel from '@/app/socialListening/components/comparisons/ComparisonFilterPanel.vue';
import TopKeywords from '@/app/socialListening/components/topic/TopKeywords.vue';
import { useSocialListeningStore } from '@/stores/social-listening';
import dayjs from 'dayjs';

const route = useRoute();
const router = useRouter();
const flagStore = useFlagStore();
const comparisonStore = useSocialListeningComparisonsStore();
const topicsStore = useSocialListeningTopicsStore();
const socialListeningStore = useSocialListeningStore();

const topPerformingPostsTopicFilterShown = ref(false);
const topicBannerClosed = ref(false);

const selectedTopicId = ref(null);

const hasSlShareOfVoice = computed(() => flagStore.ready && flagStore.flags.slShareOfVoice);

const selectedComparisonId = computed(() => (route.params?.id ? Number(route.params.id) : null));

const selectedComparison = computed(() => comparisonStore.selectedComparison);

const filteredComparisonTopics = computed(() => comparisonStore.filteredTopics);

const topicOptions = computed(() =>
  filteredComparisonTopics.value.map((topic) => ({
    label: topic.name,
    value: topic.id,
    topic,
  })),
);

const selectedTopicOption = computed(
  () => topicOptions.value.find((opt) => opt.value === selectedTopicId.value)?.topic || null,
);

const isComparisonWithTopics = computed(
  () => selectedComparison.value && selectedComparison.value.topics.length === 0,
);

const showTopicsCreatorBanner = computed(() =>
  comparisonStore.selectedComparisonTopics.some(
    (topic) => !!topic.createdAt && dayjs().diff(dayjs(topic.createdAt), 'hour') < 1,
  ),
);

const showTopicsUpdatedBanner = computed(() =>
  comparisonStore.selectedComparisonTopics.some(
    (topic) =>
      !showTopicsCreatorBanner.value &&
      !!topic.keywordsAndHashtagsUpdatedAt &&
      dayjs().diff(dayjs(topic.keywordsAndHashtagsUpdatedAt), 'hour') < 1,
  ),
);

watch(
  selectedComparisonId,
  async (newId) => {
    if (!newId) {
      return;
    }
    await comparisonStore.initLoadComparisonData(newId);
    if (!selectedComparison.value) {
      await router.push({ name: SOCIAL_LISTENING_ROUTE_NAMES.OVERVIEW });
      return;
    }

    await topicsStore.fetchBrandCompetitors();
    selectedTopicId.value = filteredComparisonTopics.value?.[0]?.id ?? null;
    comparisonViewed({
      comparisonId: selectedComparison.value?.id,
      comparisonName: selectedComparison.value?.name,
      topicIds: selectedComparison.value?.topics.map((topic) => topic.id),
    });
  },
  { immediate: true },
);

watch(filteredComparisonTopics, (newTopics) => {
  if (newTopics.length) {
    selectedTopicId.value = newTopics[0].id;
  }
});

watch(
  selectedTopicId,
  async (newId, oldId) => {
    if (newId && newId !== oldId) {
      await topicsStore.getSelectedTopicData(newId, selectedTopicOption.value);
      // Overwrite topic filters to fetch the data according to set Comparison Filters.
      topicsStore.filters = {
        keywordsAndHashtags: selectedTopicOption.value.searchBody.filters.keywordsAndHashtags,
        excludedHandles: selectedTopicOption.value.searchBody.filters.excludedHandles ?? [],
        ...comparisonStore.comparisonFilters,
      };
      await topicsStore.getTopPostsPreview();
      await topicsStore.getTopLocations();
      await socialListeningStore.getTopKeywords({
        topicId: selectedTopicId.value,
        searchFilters: topicsStore.filters,
        includeHashtags: true,
        includeKeywords: true,
      });
      await socialListeningStore.getParseTree(topicsStore.filters.keywordsAndHashtags);
    }
  },
  { immediate: true },
);

const handleBannerClose = () => {
  topicBannerClosed.value = true;
};
</script>

<template>
  <div v-if="hasSlShareOfVoice">
    <ComparisonPageHeader />
    <ComparisonFilterPanel />
    <Banner
      v-if="isComparisonWithTopics"
      class="dash-mb-12"
      severity="warn"
      message="Topics in this Comparison have been deleted."
    />
    <Section
      v-if="showTopicsCreatorBanner || showTopicsUpdatedBanner"
      :class="{ '!my-0 !transition-all !duration-700': topicBannerClosed }"
    >
      <Banner
        v-if="showTopicsCreatorBanner"
        message="Creating your Topics. Results may take up to 24 hours to populate."
        @close="handleBannerClose"
      />
      <Banner
        v-else-if="showTopicsUpdatedBanner"
        message="Updating your Topics. Results may take up to 24 hours to populate."
      />
    </Section>
    <ComparisonSummary />
    <Section>
      <MentionsTrendComparison />
    </Section>
    <TopPerformingPostsPreview key="comparisonPage">
      <template #header>
        <SectionHeader
          title="Top Performing Posts"
          :tooltip="TOPIC_COMPARISONS_TOOLTIPS.TOP_PERFORMING_POSTS"
        >
          <template #actions>
            <DropdownPanel
              v-model:shown="topPerformingPostsTopicFilterShown"
              placement="bottom-end"
            >
              <Button
                v-if="selectedTopicOption?.name"
                size="sm"
                severity="tertiary"
                icon-right="filter-lines"
                @click="topPerformingPostsTopicFilterShown = !topPerformingPostsTopicFilterShown"
              >
                Topic <span class="dash-text-subtle">is</span>
                {{ selectedTopicOption?.name }}
              </Button>

              <template #content>
                <div class="dash-flex dash-flex-col">
                  <SelectList v-model="selectedTopicId" :options="topicOptions" />
                </div>
              </template>
            </DropdownPanel>
          </template>
        </SectionHeader>
      </template>
    </TopPerformingPostsPreview>
    <SentimentAnalysisComparison />
    <Section>
      <TopKeywords>
        <template #topicChangeButton>
          <DropdownPanel placement="bottom-end">
            <Button
              v-if="selectedTopicOption?.name"
              size="sm"
              severity="tertiary"
              icon-right="filter-lines"
            >
              Topic <span class="dash-text-subtle">is</span>
              {{ selectedTopicOption?.name }}
            </Button>
            <template #content>
              <div class="dash-flex dash-flex-col">
                <SelectList v-model="selectedTopicId" :options="topicOptions" />
              </div>
            </template>
          </DropdownPanel>
        </template>
      </TopKeywords>
    </Section>
    <Demographics>
      <template #topicChangeButton>
        <DropdownPanel placement="bottom-end">
          <Button
            v-if="selectedTopicOption?.name"
            size="sm"
            severity="tertiary"
            icon-right="filter-lines"
          >
            Topic <span class="dash-text-subtle">is</span>
            {{ selectedTopicOption?.name }}
          </Button>
          <template #content>
            <div class="dash-flex dash-flex-col">
              <SelectList v-model="selectedTopicId" :options="topicOptions" />
            </div>
          </template>
        </DropdownPanel>
      </template>
    </Demographics>
    <MentionsBreakdown />
  </div>
</template>
